// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { RefObject, useMemo } from 'react';
import styled from '@emotion/styled';
import { ThemeProvider } from '@emotion/react';

import Popover from 'presentations/views/components/atoms/Popover';
import { ViewerItem } from 'presentations/api/schema/user';
import RoutineForm from 'presentations/views/components/organisms/RoutineForm';
import { DateTime } from 'infras/datetime';
import {
  fontSize,
  fontWeight,
  forceLayoutTheme,
  isMediaMax,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import { UserAppItem } from 'presentations/api/schema/account';
import Modal from 'presentations/views/components/atoms/Modal';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import {
  blueGrayPale,
  gray01,
  gray04,
  gray05,
  white,
} from 'presentations/views/styles/color';
import { Behavior } from 'domains/models/event/behavior';
import { EventCreateViewType } from 'presentations/routes/path';
import { useRouter } from 'next/router';
import { Role } from 'domains/models/event_type_user/role';
import { UserType } from 'domains/models/event_type_user/user_type';

interface Props {
  viewer: ViewerItem;
  userApp: UserAppItem | null;
  timeZone: string;
  calendarContainerRef?: RefObject<HTMLElement> | null;
  isDraggingEvent?: boolean;
  // viewType: EventCreateViewType;
  onClose: Function;
  onCreateEvent?: Function;
  element: HTMLElement | null;
  selectedUserIds: string[];
  isPopover?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const isTablet = isMediaMax('tablet');
  const maxWidth = 320;
  const anchorOrigin = useMemo(() => {
    if (!props.element) return null;
    const rect = props.element.getBoundingClientRect();
    const vertical: 'center' = 'center';
    let horizontal: 'right' | 'left' = 'right';
    if (window.innerWidth - rect.right < maxWidth) {
      horizontal = 'left';
    }
    return { vertical, horizontal };
  }, [props.element]);

  const form = (
    <ThemeProvider theme={forceLayoutTheme}>
      <RoutineForm
        viewer={props.viewer}
        routineEvent={null}
        behavior={
          Behavior.Event
          // props.viewType === EventCreateViewType.Task
          //   ? Behavior.Work
          //   : Behavior.Event
        }
        onCancel={props.onClose}
        onCreateEvent={props.onCreateEvent}
        userApp={props.userApp}
        forceTabletLayout
        isCreate
        isPopover={!isTablet}
        isPopoverOpen
        // viewType={props.viewType}
      />
    </ThemeProvider>
  );

  return isTablet ? (
    <Modal isOpen>
      <FormContainer>
        <Header>
          <HeaderTitle>{t('Create Event')}</HeaderTitle>
          <HeaderButtonContainer>
            <CloseButtonWrap onClick={props.onClose}>
              <CloseIcon name="close" />
            </CloseButtonWrap>
          </HeaderButtonContainer>
        </Header>
        <Content>{form}</Content>
      </FormContainer>
    </Modal>
  ) : props.isPopover && anchorOrigin ? (
    <Popover
      open={props.isDraggingEvent ? false : undefined}
      anchor={props.element}
      anchorOrigin={anchorOrigin}
      transformOrigin={{
        vertical: anchorOrigin.vertical,
        horizontal: anchorOrigin.horizontal === 'right' ? 'left' : 'right',
      }}
      onClose={props.onClose}
      maxWidth={320}
      disableBackdrop
    >
      {form}
    </Popover>
  ) : (
    form
  );
}

const FormContainer = styled.div`
  position: relative;
  height: 100vh;
  width: 100vw;
  background-color: ${white};
  border: 1px solid ${gray04};

  ${mediaMax.smartphone} {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    height: initial;
    height: 100dvh;
    padding-bottom: 0;
    border: none;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 57px;
  padding: ${spacing(16)};
  border-bottom: 1px solid ${gray05};
  background-color: ${white};
`;
const HeaderTitle = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
`;
const HeaderButtonContainer = styled.div`
  display: flex;
`;
const CloseButtonWrap = styled(IconButton)`
  padding: ${spacing(4)};
  margin-left: ${spacing(4)};
`;
const CloseIcon = styled(Icon)`
  color: ${gray01};
`;
const Content = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: calc(100% - 65px);
  background-color: ${blueGrayPale};
  overflow: auto;
`;
